import { REACT_APP_INSTANCE_ID } from 'env'
import { Bid, BidPrice, BidSources, USBid } from 'modules/domain/bid/types'
import { getPriceTypeLabel } from 'modules/domain/bid/utils'
import { useTranslation } from 'react-i18next'

import { CellRenders } from 'views/components/Table/renderers'

import { labelDistance } from 'modules/utils/numbers/formatDistance'
import { labelTarif } from 'modules/utils/numbers/formatTarif'

import { DASH, EXPANDABLE_COLUMN_WIDTH } from 'modules/constants'
import { labelQuantity } from 'modules/utils/numbers/formatQuantity'
import { generatePath } from 'react-router-dom'
import { BidStatusTag } from 'views/components/Tags'
import { TruncatedValue } from 'views/components/TruncatedValue/TruncatedValue'
import UserRoutes from 'views/pages/User/routes'
import { TableTextLink } from 'views/ui/TableTextLink/TableTextLink'
import { CompanyAndUserCell, ProductCell } from './styled'

const defaultWidthOpt = {
  product: 202,
  status: 118,
  volumeTon: 100,
  volumeBags: 120,
  exw: 108,
  exwTax: 146,
  cpt: 108,
  matchesCount: 86,
  address: 258,
  distance: 144,
  company: 340,
  samples: 68,
  team: 148,
  created: 112,
  created_by: 200,
  responsible: 200,
  modified: 112,
  cashPrice: 147,
  basis: 110,
  futuresPrice: 155,
  symbol: 92,
  fobBasis: 143,
  deliveryBasis: 175,
  presumableSellDate: 144,
}

const widthOptions = {
  br: {
    product: 200,
    status: 98,
    volumeTon: 120,
    exw: 130,
    cpt: 130,
    address: 192,
    distance: 124,
    company: 220,
    created: 110,
    modified: 110,
  },
  us: {
    product: 180,
    status: 94,
    firm: 60,
    volumeTon: 110,
    distance: 120,
    exw: 108,
    salePriceDelivered: 139,
    basis: 113,
    address: 240,
    created: 110,
    cpt: 130,
  },
}

const overrideWidth = widthOptions[REACT_APP_INSTANCE_ID] || {}

const columnWidths = { ...defaultWidthOpt, ...overrideWidth }

const minPeriodPriceFactory = (key: 'price_cpt' | 'price_exw') => (_, bid: Bid) => {
  return CellRenders.price(bid[key])
}

export const useBidColumns = () => {
  const { t } = useTranslation('bid')
  return {
    product: {
      title: t('list.tableHeaders.product'),
      width: columnWidths.product,
      dataIndex: 'product',
      render: ({ title }, { parameters_description, id, status, source, season }) => (
        <ProductCell
          id={id}
          title={title}
          status={status}
          parameters_description={parameters_description.split('\n')[0]}
          source={source}
          season={season?.name}
        />
      ),
    },
    status: {
      title: t('list.tableHeaders.status'),
      width: columnWidths.status,
      dataIndex: 'status',
      render: (status_, bid: Bid) => <BidStatusTag bid={bid} />,
    },
    firm: {
      title: t('list.tableHeaders.firm'),
      width: columnWidths.firm,
      dataIndex: 'is_firm',
      render: (is_firm) => <>{is_firm ? t('common:yes') : t('common:no')}</>,
    },
    quantity: {
      title: labelQuantity(),
      dataIndex: 'quantity',
      width: columnWidths.volumeTon,
      align: 'right',
      sorter: true,
      render: CellRenders.integer,
    },
    price_exw: {
      title: labelTarif(getPriceTypeLabel(BidPrice.EXW, t)),
      dataIndex: 'price_exw',
      width: columnWidths.exw,
      align: 'right',
      sorter: true,
      render: minPeriodPriceFactory('price_exw'),
    },
    price_cpt: {
      title: labelTarif(getPriceTypeLabel(BidPrice.CPT, t)),
      width: columnWidths.cpt,
      dataIndex: 'price_cpt',
      align: 'right',
      sorter: true,
      render: minPeriodPriceFactory('price_cpt'),
    },
    matches_count: {
      title: t('matchesCount'),
      width: columnWidths.matchesCount,
      dataIndex: 'matches_count',
      sorter: true,
    },
    address: {
      title: t('list.tableHeaders.address'),
      autoWidth: columnWidths.address,
      dataIndex: 'address',
      render: ({ address }) => address,
    },
    distance: {
      title: labelDistance(t('common:remoteness')),
      width: columnWidths.distance,
      dataIndex: 'distance',
      align: 'right',
      sorter: true,
      render: CellRenders.integer,
    },
    company: {
      title: t('list.tableHeaders.companyAndUser'),
      width: columnWidths.company,
      dataIndex: 'company',
      render: (company, record) => <CompanyAndUserCell company={company} owner={record.owner} />,
    },
    has_samples: {
      title: t('samples:samples'),
      width: columnWidths.samples,
      dataIndex: 'has_samples',
      render: CellRenders.haveOrNot,
    },
    owner: {
      title: t('list.tableHeaders.team'),
      width: columnWidths.team,
      dataIndex: 'owner',
      render: ({ profile }) => profile?.team?.name || null,
    },
    created: {
      title: t('list.tableHeaders.created'),
      width: columnWidths.created,
      dataIndex: 'created',
      sorter: true,
      render: CellRenders.date,
    },
    created_by: {
      title: t('list.tableHeaders.created_by'),
      width: columnWidths.created_by,
      dataIndex: 'created_by',
      render: (user, data) =>
        user?.id && (
          <TruncatedValue hint={data.source === BidSources.BARCHART ? '' : user?.full_name}>
            <TableTextLink id="bidCreatedBy" to={generatePath(UserRoutes.Details, { id: user.id })}>
              {data.source === BidSources.BARCHART ? t('common:systemUser') : user?.full_name || DASH}
            </TableTextLink>
          </TruncatedValue>
        ),
    },
    responsible: {
      title: t('common:responsible'),
      width: columnWidths.responsible,
      dataIndex: 'responsible',
      render: (user) =>
        user?.id && (
          <TruncatedValue hint={user?.full_name}>
            <TableTextLink id="responsible" to={generatePath(UserRoutes.Details, { id: user.id })}>
              {user?.full_name}
            </TableTextLink>
          </TruncatedValue>
        ),
    },
    modified: {
      title: t('list.tableHeaders.modified'),
      width: columnWidths.modified,
      dataIndex: 'modified',
      sorter: true,
      render: CellRenders.date,
    },
    empty_expandable: { width: EXPANDABLE_COLUMN_WIDTH, render: () => ' ' },
    empty: { render: () => ' ' },
    basis: {
      title: labelTarif(t('bid:basis')),
      dataIndex: 'basis',
      width: columnWidths.basis,
      align: 'right',
      sorter: true,
      render: CellRenders.price,
    },
    futuresPrice: {
      title: labelTarif(t('bid:futuresPrice')),
      // dataIndex: 'price_period',
      width: columnWidths.futuresPrice,
      align: 'right',
      sorter: true,
      render: (_, bid: USBid) => (bid.futures_price ? CellRenders.price(bid.futures_price) : undefined),
    },
    cashPrice: {
      title: labelTarif(t('bid:cashPrice')),
      dataIndex: 'price',
      width: columnWidths.cashPrice,
      align: 'right',
      render: CellRenders.price,
    },
    salePriceDelivered: {
      title: labelTarif(getPriceTypeLabel(BidPrice.CPT, t)),
      dataIndex: 'sale_price_delivered',
      width: columnWidths.cpt,
      align: 'right',
      render: CellRenders.price,
    },
    symbol: {
      title: t('bid:symbol'),
      dataIndex: 'symbol',
      width: columnWidths.symbol,
    },
    fobBasis: {
      title: labelTarif(`${t('common:fob')} ${t('basis')}`),
      dataIndex: 'price_exw_basis',
      width: columnWidths.fobBasis,
      align: 'right',
      render: CellRenders.price,
    },
    deliveryBasis: {
      title: labelTarif(`${t('common:delivered')} ${t('basis')}`),
      dataIndex: 'sale_price_delivered_basis',
      width: columnWidths.deliveryBasis,
      align: 'right',
      render: CellRenders.price,
    },
    presumableSellDate: {
      title: t('list.tableHeaders.presumableSellDate'),
      dataIndex: 'presumable_sell_date',
      width: columnWidths.presumableSellDate,
      align: 'left',
      sorter: true,
      render: CellRenders.date,
    },
  }
}
